import type { Law } from '~/utils/solr/law'
import { createCountWithFilters } from '~/utils/filter-count'
import { extractLabel, getLastName } from '#shared/utils/solr-facet'
import { isHiddenLawId } from '~/utils/law-visibility'
export interface LawFilterState {
  searchKeyword: string
  showOnlyFavorites: boolean
  hasLawComment: boolean
  selectedLegalArea: { value: string, label: string }[]
  selectedAuthor: { value: string, label: string }[]
  sortFilter: { value: string, label: string }
}

type LawDrawerOverrides = Omit<LawFilterState, 'searchKeyword' | 'sortFilter'>

export type FilteredLaw = Law & { isFavorite: boolean }

export function lawMatches(law: FilteredLaw, state: LawFilterState): boolean {
  const title = law.shortTitle || law.title || ''
  const abbreviation = law.abbreviation || ''
  const legalAreas = (law.legal_areas || []).map(area => extractLabel(area))

  if (!matchesAllSearchTerms(state.searchKeyword, [title, abbreviation, ...legalAreas])) {
    return false
  }

  if (state.hasLawComment && !law.has_commentary) {
    return false
  }

  if (state.showOnlyFavorites && !law.isFavorite) {
    return false
  }

  if (state.selectedLegalArea.length > 0) {
    const lawNames = new Set((law.legal_areas || []).map(area => extractLabel(area)))
    if (!state.selectedLegalArea.some(area => lawNames.has(extractLabel(area.value)))) {
      return false
    }
  }

  if (state.selectedAuthor.length > 0) {
    const lawAuthors = new Set((law.authors || []).map(author => extractName(author).toLowerCase()))
    if (!state.selectedAuthor.some(author => lawAuthors.has(extractName(author.value).toLowerCase()))) {
      return false
    }
  }

  if (state.selectedAuthor.length > 0) {
    const lawAuthors = new Set((law.authors || []).map(author => extractName(author).toLowerCase()))
    if (!state.selectedAuthor.some(author => lawAuthors.has(extractName(author.value).toLowerCase()))) {
      return false
    }
  }

  return true
}

export function buildLawGroups(grouped: Record<string, FilteredLaw[]>, sortValue: string): { heading: string | null, laws: FilteredLaw[] }[] {
  if (sortValue === 'kronologisk') {
    const flat = Object.values(grouped).flat()
    if (!flat.length) {
      return []
    }

    const sorted = [...flat].sort((a, b) => {
      const diff = parseToChronology(b.lastChangeInForce || b.dateInForce || '') - parseToChronology(a.lastChangeInForce || a.dateInForce || '')
      if (diff !== 0) {
        return diff
      }

      const titleA = a.shortTitleWithoutAbbreviation || a.title || ''
      const titleB = b.shortTitleWithoutAbbreviation || b.title || ''
      return compareLocaleNorwegian(titleA, titleB)
    })
    return [{ heading: null, laws: sorted }]
  }
  return Object.entries(grouped).map(([heading, laws]) => ({ heading, laws }))
}

export function buildAuthorOptions(docs: { authors?: string[] | null }[]): { value: string, label: string }[] {
  const byName = new Map<string, string>()

  for (const doc of docs) {
    for (const author of doc.authors || []) {
      const key = extractName(author).toLowerCase()
      if (!byName.has(key)) {
        byName.set(key, author)
      }
    }
  }

  return Array.from(byName.values())
    .map(author => ({ value: author, label: extractName(author) }))
    .sort((a, b) => compareLocaleNorwegian(getLastName(a.label).toLowerCase(), getLastName(b.label).toLowerCase()))
}

export const LAW_FACET_CONFIG = {
  selectedLegalArea: {
    docKeys: (law: Law) => (law.legal_areas || []).map(area => extractLabel(area)),
    optionKey: value => extractLabel(value),
  },
  selectedAuthor: {
    docKeys: (law: Law) => (law.authors || []).map(author => extractName(author).toLowerCase()),
    optionKey: value => extractName(value).toLowerCase(),
  },
} as const satisfies Partial<Record<FacetKeys<LawFilterState>, FacetIndexConfig<Law>>>

export function _useLawFilters() {
  const searchKeyword = useState<string>('law-filter-search', () => '')
  const showOnlyFavorites = useState<boolean>('law-filter-favorites', () => false)
  const hasLawComment = useState<boolean>('law-filter-commentary', () => false)
  const sortFilter = useState<{ value: string, label: string }>('law-filter-sort', () => LAW_SORTING_OPTIONS[0])
  const selectedAuthor = useState<{ value: string, label: string }[]>('law-filter-author', () => [])
  const selectedLegalArea = useState<{ value: string, label: string }[]>('law-filter-legal-area', () => [])

  const searchLegalArea = ref<string>('')
  const searchAuthor = ref<string>('')

  const { data: solrData, isLoading, suspense } = useLawQuery()

  const { isFavorite } = useFavorites()

  const totalLaws = computed(() => solrData.value?.response.docs.length ?? 0)

  const hasFilters = computed(() => {
    return searchKeyword.value.length > 0 || showOnlyFavorites.value || hasLawComment.value || selectedLegalArea.value.length > 0 || selectedAuthor.value.length > 0
  })

  const sortedSolrLaws = computed<FilteredLaw[]>(() => {
    const docs = (solrData.value?.response.docs ?? []).filter(doc => !isHiddenLawId(doc.id))

    if (!docs.length) {
      return []
    }

    return [...docs].sort((a, b) => {
      const titleA = a.shortTitle || a.title || ''
      const titleB = b.shortTitle || b.title || ''
      return compareLocaleNorwegian(titleA, titleB)
    }).map(doc => ({ ...doc, isFavorite: isFavorite('law', doc.id) }))
  })

  const legalAreaOptions = computed(() => {
    const docs = sortedSolrLaws.value
    const allLegalAreas = new Set<string>()

    for (const doc of docs) {
      for (const area of doc.legal_areas) {
        allLegalAreas.add(area)
      }
    }

    return Array.from(allLegalAreas)
      .map(area => ({ value: area, label: extractLabel(area) }))
      .sort((a, b) => compareLocaleNorwegian(a.label, b.label))
  })

  const authorOptions = computed(() => buildAuthorOptions(sortedSolrLaws.value))

  const groupedLaws = computed<Record<string, FilteredLaw[]>>(() => {
    if (!sortedSolrLaws.value?.length) {
      return {}
    }

    return sortedSolrLaws.value.reduce((acc, law) => {
      const title = law.shortTitle || law.title || ''
      const firstLetter = title?.charAt(0).toUpperCase()
      acc[firstLetter] = acc[firstLetter] || []
      acc[firstLetter].push(law)
      return acc
    }, {} as Record<string, FilteredLaw[]>)
  })

  const filteredLaws = computed<Record<string, FilteredLaw[]>>(() => {
    if (!hasFilters.value) {
      return groupedLaws.value
    }

    const state = committedState.value

    return Object.entries(groupedLaws.value).reduce((acc, [key, laws]) => {
      const filteredLaws = laws.filter(law => lawMatches(law, state))
      if (filteredLaws.length) {
        acc[key] = filteredLaws
      }
      return acc
    }, {} as Record<string, FilteredLaw[]>)
  })

  const lawGroups = computed(() => buildLawGroups(filteredLaws.value, sortFilter.value.value))

  const totalFilteredLaws = computed(() => {
    return Object.values(filteredLaws.value).reduce((acc, laws) => acc + laws.length, 0)
  })

  const committedState = computed<LawFilterState>(() => ({
    searchKeyword: searchKeyword.value,
    showOnlyFavorites: showOnlyFavorites.value,
    hasLawComment: hasLawComment.value,
    selectedLegalArea: selectedLegalArea.value,
    selectedAuthor: selectedAuthor.value,
    sortFilter: sortFilter.value,
  }))

  const countForFacet = createCountWithFilters(sortedSolrLaws, committedState, lawMatches)

  const facetCounts = useFacetIndex(sortedSolrLaws, committedState, lawMatches, LAW_FACET_CONFIG)

  const { selected: legalAreaSelected, toggle: toggleLegalArea, facetOptions: legalAreaFacetOptions } = useFacetFilter(selectedLegalArea, legalAreaOptions, facetCounts.selectedLegalArea)

  const { selected: authorSelected, toggle: toggleAuthor, facetOptions: authorFacetOptions } = useFacetFilter(selectedAuthor, authorOptions, facetCounts.selectedAuthor)

  const hasLawCommentCount = computed(() => countWithFilters({ hasLawComment: true }))

  function countWithFilters(overrides: Partial<LawDrawerOverrides>): number {
    return countForFacet(overrides)
  }

  function toggleFavorite() {
    showOnlyFavorites.value = !showOnlyFavorites.value
  }

  function toggleHasLawComment() {
    hasLawComment.value = !hasLawComment.value
  }

  return {
    isLoading,
    suspense,
    totalLaws,
    showOnlyFavorites,
    hasLawComment,
    searchKeyword,
    hasFilters,
    toggleFavorite,
    toggleHasLawComment,
    totalFilteredLaws,
    selectedAuthor,
    sortFilter,
    legalAreaOptions,
    authorOptions,
    selectedLegalArea,
    countWithFilters,
    toggleLegalArea,
    toggleAuthor,
    searchLegalArea,
    searchAuthor,
    legalAreaFacetOptions,
    authorFacetOptions,
    hasLawCommentCount,
    lawGroups,
    legalAreaSelected,
    authorSelected,
  }
}

export const useLawFilters = createSharedComposable(_useLawFilters)
